import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu as MenuIcon, X, ShoppingBag } from "lucide-react";
import { CONTACT } from "../data/menu";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Souvláki", href: "#souvlaki" },
  { label: "Menu", href: "#menu" },
  { label: "Visit", href: "#visit" },
];

export const Nav = () => {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const glassy = solid || open;

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 transition-colors duration-500"
      style={{
        background: glassy ? "rgba(255,255,255,0.82)" : "transparent",
        backdropFilter: glassy ? "blur(18px)" : "none",
        borderBottom: glassy ? "1px solid var(--hair)" : "1px solid transparent",
      }}
    >
      <nav className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <img
            src="/logo-transparent.png"
            alt="Lucky Corner Fish, Chippery & Souvláki"
            className="h-16 w-16 object-contain transition-transform duration-500 group-hover:rotate-[8deg]"
          />
          <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--navy)] hidden sm:block leading-tight">
            Lucky&nbsp;Corner
            <span className="block text-[8px] tracking-[0.34em] text-[var(--slate)]">Fish, Chippery &amp; Souvláki</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="relative font-mono text-[12px] tracking-[0.2em] uppercase text-[var(--ink)] py-1 group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[var(--azure)] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href={CONTACT.orderUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-order-btn"
            className="inline-flex items-center gap-2 rounded-full px-3.5 sm:px-5 py-2.5 text-white text-[12px] font-mono tracking-[0.15em] uppercase transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            style={{ background: "var(--azure)" }}
          >
            <ShoppingBag size={14} strokeWidth={2.4} />
            <span className="hidden sm:inline">Online Order</span>
          </a>
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-btn"
            aria-label="Call to order"
            className="grid place-items-center w-11 h-11 rounded-full text-white transition-transform duration-300 hover:scale-[1.06] active:scale-95"
            style={{ background: "var(--navy)" }}
          >
            <Phone size={15} strokeWidth={2.4} />
          </a>

          <a
            href="#menu"
            data-testid="nav-mobile-menu-tab"
            className="md:hidden inline-flex items-center rounded-full px-3.5 py-2.5 text-[12px] font-mono tracking-[0.15em] uppercase border border-[var(--hair)] text-[var(--navy)] bg-white/60"
          >
            Menu
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="md:hidden grid place-items-center w-11 h-11 rounded-full border border-[var(--hair)] text-[var(--navy)] bg-white/60"
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-[var(--hair)]"
            style={{ background: "#ffffff" }}
          >
            <div className="px-6 py-4 flex flex-col">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl text-[var(--navy)] py-3 border-b border-[var(--hair)]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={CONTACT.orderUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-order"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-white text-[13px] font-mono tracking-[0.15em] uppercase"
                style={{ background: "var(--azure)" }}
              >
                <ShoppingBag size={15} strokeWidth={2.4} />
                Online Order
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
